<template>
  <!--
    SessionDetailsDialog.vue

    Modal dialog for displaying session details and registered users.
    Features:
      - Session information display
      - Users data table with registration and attendance details
      - Search and export functionality for users
      - Responsive Vuetify dialog
  -->
  <v-dialog v-model="dialog" persistent max-width="1200">
    <v-card v-if="session">
      <v-card-title class="text-h5 primary white--text">
        <v-icon left dark>mdi-calendar-account</v-icon>
        {{ session.name }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Session Information -->
        <v-row class="mb-4">
          <v-col cols="12">
            <h3 class="text-h6 mb-3">Session Information</h3>
            <v-row>
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Description</v-list-item-title>
                      <v-list-item-subtitle>{{ session.description }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Start Date & Time</v-list-item-title>
                      <v-list-item-subtitle>{{ session.dateStart | readableDateTime }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>End Date & Time</v-list-item-title>
                      <v-list-item-subtitle>{{ session.dateEnd | readableDateTime }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="12" md="6">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Registration Limit</v-list-item-title>
                      <v-list-item-subtitle>{{ session.limit }} participants</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Registered Count</v-list-item-title>
                      <v-list-item-subtitle>{{ session.registrantsCount }} / {{ session.limit }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Attended Count</v-list-item-title>
                      <v-list-item-subtitle>{{ session.attendantsCount }} participants</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Users Section -->
        <div class="d-flex justify-space-between align-center mb-4">
          <h3 class="text-h6">Registered Users</h3>
          <div class="d-flex align-center">
            <!-- Search field -->
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search users"
              clearable
              single-line
              hide-details
              class="mr-2"
              style="max-width: 300px;"
            ></v-text-field>
            <!-- Refresh button -->
            <v-btn color="primary" small @click="loadSessionDetails">
              <v-icon small>mdi-reload</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Users data table -->
        <v-data-table
          :loading="sessionDetailsLoading"
          :headers="userTableHeaders"
          :items="filteredUsersWithKeys"
          :items-per-page="10"
          loading-text="Loading users... Please wait"
          item-key="uniqueKey"
          class="elevation-1"
        >
          <!-- Registration status column -->
          <template v-slot:item.isRegistered="{ item }">
            <v-chip
              :color="item.isRegistered ? 'success' : 'default'"
              :text-color="item.isRegistered ? 'white' : 'black'"
              small
            >
              {{ item.isRegistered ? 'Registered' : 'Not Registered' }}
            </v-chip>
          </template>

          <!-- Date registered column -->
          <template v-slot:item.dateRegistered="{ item }">
            {{ item.dateRegistered | readableDateTime }}
          </template>

          <!-- Date attended column -->
          <template v-slot:item.dateAttended="{ item }">
            <span v-if="item.dateAttended">{{ item.dateAttended | readableDateTime }}</span>
            <span v-else class="text--secondary">Not attended</span>
          </template>

          <!-- Attendance status column -->
          <template v-slot:item.attendanceStatus="{ item }">
            <v-chip
              :color="getAttendanceStatusColor(item)"
              :text-color="getAttendanceStatusTextColor(item)"
              small
            >
              {{ getAttendanceStatusText(item) }}
            </v-chip>
          </template>
        </v-data-table>

        <!-- Users summary -->
        <div class="mt-4 text-center">
          <v-chip class="ma-1" color="info" text-color="white">
            Total: {{ users.length }}
          </v-chip>
          <v-chip class="ma-1" color="success" text-color="white">
            Registered: {{ registeredUsersCount }}
          </v-chip>
          <v-chip class="ma-1" color="warning" text-color="white">
            Attended: {{ attendedUsersCount }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { format } from 'date-fns';

export default {
  name: 'SessionDetailsDialog',
  filters: {
    /**
     * Converts a date string to a human-readable date time format.
     * @param {string} value - The date string.
     * @returns {string} Formatted date and time.
     */
    readableDateTime: (value) => {
      if (!value) return '';
      return format(new Date(value), 'MMM d, y h:mm a');
    },
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      userSearch: '',
      userTableHeaders: [
        {
          text: 'First Name',
          align: 'start',
          sortable: true,
          value: 'firstName',
        },
        {
          text: 'Last Name',
          value: 'lastName',
          sortable: true,
        },
        {
          text: 'User ID',
          value: 'userId',
          sortable: true,
        },
        {
          text: 'Registration Status',
          value: 'isRegistered',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Date Registered',
          value: 'dateRegistered',
          sortable: true,
        },
        {
          text: 'Date Attended',
          value: 'dateAttended',
          sortable: true,
        },
        {
          text: 'Attendance Status',
          value: 'attendanceStatus',
          sortable: false,
          align: 'center',
        },
      ],
    };
  },
  computed: {
    ...mapState(['sessionDetails', 'sessionDetailsLoading']),

    /**
     * Gets users from session details
     * @returns {Array} Users array
     */
    users() {
      return this.sessionDetails?.users || [];
    },

    /**
     * Filters users based on search input
     * @returns {Array} Filtered users array
     */
    filteredUsers() {
      if (!this.userSearch) {
        return this.users;
      }

      const search = this.userSearch.toLowerCase();
      return this.users.filter((user) => (user.firstName && user.firstName.toLowerCase().includes(search))
        || (user.lastName && user.lastName.toLowerCase().includes(search))
        || (user.userId && user.userId.toLowerCase().includes(search)));
    },

    /**
     * Adds unique keys to filtered users to prevent duplicate key errors
     * @returns {Array} Filtered users array with unique keys
     */
    filteredUsersWithKeys() {
      return this.filteredUsers.map((user, index) => ({
        ...user,
        uniqueKey: `${user.userId}-${index}`,
      }));
    },

    /**
     * Count of registered users
     * @returns {number} Number of registered users
     */
    registeredUsersCount() {
      return this.users.filter((user) => user.isRegistered).length;
    },

    /**
     * Count of attended users
     * @returns {number} Number of attended users
     */
    attendedUsersCount() {
      return this.users.filter((user) => user.dateAttended).length;
    },
  },
  watch: {
    /**
     * Watch for dialog changes to load session details
     */
    dialog(newValue) {
      if (newValue && this.session) {
        this.loadSessionDetails();
      }
    },
  },
  methods: {
    ...mapActions(['getSessionDetailsAction']),

    /**
     * Loads session details with users
     */
    async loadSessionDetails() {
      if (this.session?.id) {
        await this.getSessionDetailsAction(this.session.id);
      }
    },

    /**
     * Gets attendance status color
     * @param {Object} user - The user object
     * @returns {string} Color name
     */
    getAttendanceStatusColor(user) {
      if (user.dateAttended) return 'success';
      if (user.isRegistered) return 'default';
      return 'default';
    },

    /**
     * Gets attendance status text color
     * @param {Object} user - The user object
     * @returns {string} Text color
     */
    getAttendanceStatusTextColor(user) {
      if (user.dateAttended || user.isRegistered) return 'white';
      return 'black';
    },

    /**
     * Gets attendance status text
     * @param {Object} user - The user object
     * @returns {string} Status text
     */
    getAttendanceStatusText(user) {
      if (user.dateAttended) return 'Attended';
      if (user.isRegistered) return 'Not Attended';
      return 'Not Registered';
    },
  },
};
</script>

<style scoped>
.v-list-item {
  min-height: 36px;
}
</style>
